import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getApiErrorMessage,
  showAlert,
  SituacaoTranscricaoType,
} from '../../../../../../_metronic/helpers'
import { getTranscricoesAtendimento } from '../../../atendimento/core/_requests'
import { iniciarStt, pararStt, sttUrl } from '../core/_requests'

export const useStt = (idAtendimento: number, habilitado = true) => {
  const [isSttEnabled, setIsSttEnabled] = useState(false)
  const [sttPendente, setSttPendente] = useState(false)
  const ativoRef = useRef(false)
  const pendenteRef = useRef(false)

  useEffect(() => {
    if (!habilitado) return
    let cancelado = false
    getTranscricoesAtendimento(idAtendimento)
      .then((sessoes) => {
        if (
          !cancelado &&
          sessoes.some((s) => s.situacao === SituacaoTranscricaoType.Transcrevendo)
        ) {
          ativoRef.current = true
          setIsSttEnabled(true)
        }
      })
      .catch((error) => {
        if (!cancelado) {
          console.error(
            'useStt: falha ao consultar as transcrições do atendimento',
            idAtendimento,
            error
          )
        }
      })
    return () => {
      cancelado = true
    }
  }, [idAtendimento, habilitado])

  useEffect(() => {
    if (!habilitado) return
    const deveParar = () => ativoRef.current && !pendenteRef.current

    const handleBeforeUnload = () => {
      if (deveParar()) {
        fetch(sttUrl(idAtendimento), { method: 'DELETE', keepalive: true, credentials: 'include' })
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      if (deveParar()) {
        pararStt(idAtendimento).catch((error) => {
          console.error(
            'useStt: falha ao encerrar a transcrição do atendimento',
            idAtendimento,
            error
          )
        })
      }
    }
  }, [idAtendimento, habilitado])

  const toggleStt = useCallback(async () => {
    if (pendenteRef.current) {
      return
    }
    pendenteRef.current = true
    setSttPendente(true)
    const estadoAnterior = ativoRef.current
    try {
      if (estadoAnterior) {
        await pararStt(idAtendimento)
      } else {
        await iniciarStt(idAtendimento)
      }
      ativoRef.current = !estadoAnterior
      setIsSttEnabled(!estadoAnterior)
    } catch (error) {
      const generico = estadoAnterior
        ? 'Erro ao desabilitar legendas.'
        : 'Erro ao habilitar legendas.'
      const resposta = (error as { response?: { status?: number; data?: { message?: unknown } } })
        ?.response
      // O 422 do back vem no envelope ({data: {message}}); o formato solto fica por compatibilidade.
      const mensagem = getApiErrorMessage(error) ?? resposta?.data?.message
      showAlert({
        title: 'Erro!',
        text:
          resposta?.status === 422 && typeof mensagem === 'string' && mensagem.trim()
            ? mensagem
            : generico,
        icon: 'error',
      })
    } finally {
      pendenteRef.current = false
      setSttPendente(false)
    }
  }, [idAtendimento])

  return { isSttEnabled, sttPendente, toggleStt }
}
